import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../../lib/utils';

const fieldErrorClasses =
    '[&_input]:ring-2 [&_input]:ring-red-500 [&_button[role=combobox]]:ring-2 [&_button[role=combobox]]:ring-red-500';
import { Button } from '../../ui/button';

export interface DialogFrameProps {
    open: boolean;
    title: string;
    description?: string;
    onClose: () => void;
    children: ReactNode;
    panelClassName?: string;
}

export function DialogFrame({
    open,
    title,
    description,
    onClose,
    children,
    panelClassName,
}: DialogFrameProps) {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div
                className={cn(
                    'max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl',
                    panelClassName,
                )}
            >
                <div className="flex items-start justify-between gap-4 border-b p-5">
                    <div>
                        <h2 className="text-xl font-semibold text-neutral-950">{title}</h2>
                        {description ? (
                            <p className="mt-1 text-sm text-neutral-500">{description}</p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                    >
                        <X className="size-5" />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

export function FormErrorAlert({ message }: { message: string }) {
    return (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
            {message}
        </p>
    );
}

export function Field({
    children,
    label,
    hint,
    required,
    error,
}: {
    children: ReactNode;
    label: string;
    hint?: string;
    required?: boolean;
    error?: string;
}) {
    return (
        <label className={cn('block space-y-1', error && fieldErrorClasses)}>
            <span className="text-sm font-semibold text-neutral-950">
                {label}
                {required ? (
                    <span className="text-red-600" aria-hidden="true">
                        {' '}
                        *
                    </span>
                ) : null}
            </span>
            {children}
            {error ? (
                <span className="block text-xs text-red-600" role="alert">
                    {error}
                </span>
            ) : hint ? (
                <span className="block text-xs text-neutral-600">{hint}</span>
            ) : null}
        </label>
    );
}

export const AUDIT_REASON_FIELD_HINT =
    'For auditing: saved with this action so the history can be reviewed later.';

export function Actions({ children, onCancel }: { children: ReactNode; onCancel: () => void }) {
    return (
        <div className="flex justify-end gap-2 pt-4">
            <Button
                type="button"
                variant="outline"
                className="border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50"
                onClick={onCancel}
            >
                Cancel
            </Button>
            {children}
        </div>
    );
}
